import { useEffect, useState, type FormEvent } from "react";
import { apiGet, apiPost, type Organization, type Service } from "../api";
import { Icons } from "./icons";
import { CategoryCombobox } from "./CategoryCombobox";

const SLUG_PATTERN = /^[a-z0-9._-]+$/;

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (svc: Service) => void;
  showOrgPicker?: boolean;
  orgs?: Organization[];
}

export function ServiceCreateModal({ open, onClose, onCreated, showOrgPicker = false, orgs = [] }: Props) {
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [icon, setIcon] = useState("");
  const [orgId, setOrgId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<string[]>([]);

  const loadCategories = () =>
    apiGet<{ categories: string[] }>("/v1/services/categories")
      .then((r) => setCategories(r.categories))
      .catch(() => setCategories([]));

  useEffect(() => {
    if (open) loadCategories();
  }, [open]);

  if (!open) return null;

  const slugInvalid = slug !== "" && !SLUG_PATTERN.test(slug);
  const canSubmit =
    slug !== "" &&
    name !== "" &&
    category !== "" &&
    !slugInvalid &&
    !submitting &&
    (!showOrgPicker || orgId !== "");

  function reset() {
    setSlug("");
    setName("");
    setCategory("");
    setIcon("");
    setOrgId("");
    setError(null);
  }

  function handleClose() {
    if (submitting) return;
    reset();
    onClose();
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const body: Record<string, string | null> = {
        slug: slug.trim(),
        name: name.trim(),
        category: category.trim().toLowerCase(),
        icon: icon.trim() || null,
      };
      if (showOrgPicker && orgId) body.org_id = orgId;
      const svc = await apiPost<Service>("/v1/services", body);
      onCreated(svc);
      reset();
      onClose();
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message.toLowerCase().includes("unique") ||
            err.message.toLowerCase().includes("conflict") ||
            err.message.toLowerCase().includes("already")
            ? `El slug "${slug}" ya existe en esta organización — probá con otro.`
            : err.message
          : "Error desconocido";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div onClick={handleClose} className="modal-backdrop">
      <div
        onClick={(e) => e.stopPropagation()}
        className="modal-card"
        style={{ width: 560, maxWidth: "100%", display: "grid", gap: 14, padding: 20 }}
      >
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontFamily: "var(--font-display)", letterSpacing: "-0.01em" }}>
              Nuevo servicio
            </h2>
            <p className="text-sm text-muted" style={{ marginTop: 4, marginBottom: 0 }}>
              Registrá un sistema externo para que los eventos con su <code className="cfg-code">service_id</code>{" "}
              se agreguen en el dashboard.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-icon btn-ghost"
            onClick={handleClose}
            disabled={submitting}
            aria-label="Cerrar"
          >
            <Icons.Close size={14} />
          </button>
        </header>

        <form onSubmit={onSubmit} style={{ display: "grid", gap: 12 }}>
          {showOrgPicker && (
            <label style={{ display: "grid", gap: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "var(--fg-2)" }}>Organización</span>
              <select className="input" value={orgId} onChange={(e) => setOrgId(e.target.value)} required>
                <option value="" disabled>Elegí una organización…</option>
                {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </label>
          )}
          <Field
            label="Slug"
            hint='Identificador legible, único por organización. Formato "categoria.nombre". Ejemplo: identidad.renaper'
            invalidHint={slugInvalid ? "Solo lowercase, números, punto, guion y guion bajo." : null}
          >
            <input
              className="input"
              autoFocus
              placeholder="identidad.mi_servicio"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              spellCheck={false}
              autoCapitalize="off"
              autoCorrect="off"
              style={
                slugInvalid
                  ? { borderColor: "var(--st-major)", outlineColor: "var(--st-major)" }
                  : undefined
              }
            />
          </Field>

          <Field label="Nombre" hint="Cómo aparece en la UI.">
            <input
              className="input"
              placeholder="Renaper"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>

          <Field
            label="Categoría"
            hint="Elegí de la lista o creá una nueva. También se gestionan desde Configuración → Organización."
          >
            <CategoryCombobox
              value={category}
              categories={categories}
              onChange={setCategory}
              onCreated={() => loadCategories()}
            />
          </Field>

          <Field
            label="Icono (opcional)"
            hint="URL o slug de icono. Si lo dejás vacío, se usa el icono de la categoría."
          >
            <input
              className="input"
              placeholder="(opcional)"
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
            />
          </Field>

          {error && (
            <div
              role="alert"
              style={{
                background: "color-mix(in oklab, var(--st-major) 12%, transparent)",
                border: "1px solid color-mix(in oklab, var(--st-major) 35%, transparent)",
                color: "var(--st-major)",
                padding: "8px 10px",
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              {error}
            </div>
          )}

          <footer style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 6 }}>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
              {submitting ? "Creando…" : "Crear servicio"}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  invalidHint,
  children,
}: {
  label: string;
  hint?: string;
  invalidHint?: string | null;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: "grid", gap: 4 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: "var(--fg-2)" }}>{label}</span>
      {children}
      {invalidHint ? (
        <span className="hint" style={{ color: "var(--st-major)" }}>{invalidHint}</span>
      ) : (
        hint && <span className="hint">{hint}</span>
      )}
    </label>
  );
}
